// src/Reports.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  IconButton,
  Paper,
  Divider,
  Chip,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Snackbar,
  Alert,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/system';
import jsPDF from 'jspdf';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import ReportIcon from '@mui/icons-material/Report';
import AddIcon from '@mui/icons-material/Add';
import { serverTimestamp } from 'firebase/firestore';
import { useAuth } from './AuthContext';

// Firestore helpers
import {
  getAllItems,
  addItem,
  updateItem,
  deleteItem,
  loadUserSingleDoc,
  saveUserSingleDoc,
} from './firestore';

// ---- Premium buttons (same vibe as Invoices) ----
const PrimaryButton = styled(Button)({
  borderRadius: 12,
  padding: '10px 16px',
  textTransform: 'none',
  fontWeight: 900,
  boxShadow: 'none',
  backgroundColor: '#000',
  color: '#fff',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
  '&:hover': {
    backgroundColor: '#222',
    transform: 'translateY(-1px)',
    boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
  },
  '&:disabled': { backgroundColor: '#111', color: 'rgba(255,255,255,0.6)' },
});

const SubtleButton = styled(Button)({
  borderRadius: 12,
  padding: '10px 16px',
  textTransform: 'none',
  fontWeight: 900,
  boxShadow: 'none',
  backgroundColor: '#fff',
  color: '#000',
  border: '1px solid rgba(0,0,0,0.15)',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
  '&:hover': {
    backgroundColor: '#fafafa',
    transform: 'translateY(-1px)',
    boxShadow: '0 10px 24px rgba(0,0,0,0.08)',
  },
});

const StyledSelect = styled(Select)({
  backgroundColor: '#ffffff',
  color: '#000000',
  '& .MuiSelect-icon': { color: '#000000' },
});

// ✅ dropdown menu fix (white background, black text)
const menuProps = {
  PaperProps: {
    sx: {
      bgcolor: '#ffffff',
      color: '#000000',
      '& .MuiMenuItem-root': {
        color: '#000000',
        '&:hover': { backgroundColor: '#f5f5f5' },
        '&.Mui-selected': { backgroundColor: '#e0e0e0', '&:hover': { backgroundColor: '#d0d0d0' } },
      },
    },
  },
};

const SoftCard = ({ title, icon, right, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, sm: 2.5 },
      borderRadius: 4,
      border: '1px solid rgba(0,0,0,0.08)',
      bgcolor: 'white',
      boxShadow: '0 18px 45px rgba(0,0,0,0.05)',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 999,
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'rgba(0,0,0,0.06)',
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 220, flex: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 900 }}>
          {title}
        </Typography>
      </Box>

      {right}
    </Box>

    <Divider sx={{ my: 2 }} />

    {children}
  </Paper>
);

const StatPill = ({ label, value }) => (
  <Paper
    elevation={0}
    sx={{
      px: 1.4,
      py: 0.9,
      borderRadius: 999,
      border: '1px solid rgba(0,0,0,0.08)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      bgcolor: 'rgba(255,255,255,0.85)',
      backdropFilter: 'blur(6px)',
    }}
  >
    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)', fontWeight: 800 }}>
      {label}
    </Typography>
    <Chip
      size="small"
      label={value}
      sx={{ fontWeight: 900, bgcolor: 'rgba(0,0,0,0.06)', borderRadius: 999 }}
    />
  </Paper>
);

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = String(dateStr).split('-');
  if (!year || !month || !day) return String(dateStr);
  return `${day}/${month}/${year}`;
};

const todayISO = () => new Date().toISOString().split('T')[0];

const Reports = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [reports, setReports] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [userInfo, setUserInfo] = useState({});
  const [participantId, setParticipantId] = useState('');
  const [incidentDate, setIncidentDate] = useState('');

  const [reportData, setReportData] = useState({
    incidentType: '',
    description: '',
    location: '',
    severity: '',
    actionsTaken: '',
  });

  const [editingId, setEditingId] = useState(null);
  const [nextReportId, setNextReportId] = useState(1);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState('');
  const [showArchivedParticipants, setShowArchivedParticipants] = useState(false);

  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  const showToast = (severity, message) => setToast({ open: true, severity, message });

  // Load all data on mount
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const loadData = async () => {
      try {
        const [loadedReports, loadedParticipants, loadedUserInfo, reportSettings] = await Promise.all([
          getAllItems(currentUser.uid, 'incidentReports'),
          getAllItems(currentUser.uid, 'participants'),
          loadUserSingleDoc(currentUser.uid, 'userInfo', {}),
          loadUserSingleDoc(currentUser.uid, 'settings', { nextReportId: 1 }),
        ]);

        setReports(loadedReports || []);
        setParticipants(loadedParticipants || []);
        setUserInfo(loadedUserInfo || {});
        setNextReportId(reportSettings?.nextReportId || 1);
      } catch (error) {
        console.error('Error loading data:', error);
        showToast('error', 'Failed to load reports/participants.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, navigate]);

  const visibleParticipants = useMemo(() => {
    const list = (participants || []).slice();
    const filtered = showArchivedParticipants ? list : list.filter((p) => !p.archived);
    return filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [participants, showArchivedParticipants]);

  const saveNextReportId = async (newId) => {
    setNextReportId(newId);
    await saveUserSingleDoc(currentUser.uid, 'settings', {
      nextReportId: newId,
      updatedAt: serverTimestamp(),
    });
  };

  const resetForm = () => {
    setParticipantId('');
    setIncidentDate('');
    setReportData({
      incidentType: '',
      description: '',
      location: '',
      severity: '',
      actionsTaken: '',
    });
    setEditingId(null);
  };

  const handleFormChange = (field) => (e) => {
    setReportData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const reportNo = (r) => `IR-${String(r?.reportNumber || 0).padStart(4, '0')}`;

  const stats = useMemo(() => {
    const list = reports || [];
    const total = list.length;
    const critical = list.filter((r) => String(r.severity || '').toLowerCase() === 'critical').length;
    const high = list.filter((r) => String(r.severity || '').toLowerCase() === 'high').length;
    const monthPrefix = todayISO().slice(0, 7); // YYYY-MM
    const thisMonth = list.filter((r) => String(r.incidentDate || '').startsWith(monthPrefix)).length;
    return { total, critical, high, thisMonth };
  }, [reports]);

  const filteredReports = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = (reports || []).filter((r) => {
      if (!q) return true;
      const p = participants.find((x) => x.id === r.participantId);
      const name = (r.participantSnapshot?.name || p?.name || '').toLowerCase();
      const ndis = (r.participantSnapshot?.ndisNumber || p?.ndisNumber || '').toLowerCase();
      const idStr = reportNo(r).toLowerCase();
      const type = String(r.incidentType || '').toLowerCase();
      const sev = String(r.severity || '').toLowerCase();
      return (
        name.includes(q) ||
        ndis.includes(q) ||
        idStr.includes(q) ||
        type.includes(q) ||
        sev.includes(q) ||
        String(r.incidentDate || '').includes(q)
      );
    });

    return base
      .slice()
      .sort((a, b) => String(b?.incidentDate || '').localeCompare(String(a?.incidentDate || '')));
  }, [reports, participants, query]);

  // -------- PDF (premium layout) --------
  const generatePDF = (report) => {
    const participant = participants.find((p) => p.id === report.participantId);

    const pName = report.participantSnapshot?.name || participant?.name || 'Unknown';
    const pNdis = report.participantSnapshot?.ndisNumber || participant?.ndisNumber || 'Unknown';

    const provider = report.providerSnapshot || userInfo || {};

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 14;

    const safe = (v) => (v ? String(v) : '');
    const wrap = (text, w) => doc.splitTextToSize(String(text || ''), w);

    // top rule
    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.line(margin, 10, pageW - margin, 10);

    // title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('NDIS INCIDENT REPORT', margin, 22);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80);
    doc.text('Incident documentation for NDIS supports', margin, 28);
    doc.setTextColor(0);

    // right meta
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Report ID', pageW - margin, 18, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    doc.text(reportNo(report), pageW - margin, 23, { align: 'right' });

    doc.setFont('helvetica', 'bold');
    doc.text('Incident Date', pageW - margin, 30, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    doc.text(formatDate(report.incidentDate), pageW - margin, 35, { align: 'right' });

    // section helper
    const sectionTitle = (t, y) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text(t, margin, y);
      doc.setDrawColor(200);
      doc.setLineWidth(0.4);
      doc.line(margin, y + 2, pageW - margin, y + 2);
      doc.setDrawColor(0);
      doc.setLineWidth(0.2);
    };

    const leftX = margin;
    const rightX = pageW / 2 + 4;
    

    let y = 46;

    sectionTitle('Participant & Provider', y);
    y += 10;

    // Participant block
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Participant', leftX, y);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const pLines = [safe(pName), `NDIS Number: ${safe(pNdis)}`];
    pLines.forEach((ln, i) => doc.text(ln, leftX, y + 6 + i * 5));

    // Provider block
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Reported By', rightX, y);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const providerLines = [
      safe(provider.name) || 'Provider name not set',
      `ABN: ${safe(provider.abn) || '(not provided)'}`,
      safe(provider.ndisProviderNumber) ? `NDIS Provider #: ${safe(provider.ndisProviderNumber)}` : null,
    ].filter(Boolean);
    providerLines.forEach((ln, i) => doc.text(ln, rightX, y + 6 + i * 5));

    y += 26;

    sectionTitle('Incident Summary', y);
    y += 10;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Type', leftX, y);
    doc.text('Severity', rightX, y);

    doc.setFont('helvetica', 'normal');
    doc.text(safe(report.incidentType), leftX, y + 6);
    doc.text(safe(report.severity), rightX, y + 6);

    y += 18;

    doc.setFont('helvetica', 'bold');
    doc.text('Location', leftX, y);
    doc.setFont('helvetica', 'normal');
    const locLines = wrap(safe(report.location), pageW - margin * 2);
    doc.text(locLines, leftX, y + 6);
    y += locLines.length * 5 + 10;

    sectionTitle('Details', y);
    y += 10;

    doc.setFont('helvetica', 'bold');
    doc.text('Description', leftX, y);
    doc.setFont('helvetica', 'normal');
    const descLines = wrap(safe(report.description), pageW - margin * 2);
    doc.text(descLines, leftX, y + 6);
    y += descLines.length * 5 + 10;

    doc.setFont('helvetica', 'bold');
    doc.text('Actions Taken', leftX, y);
    doc.setFont('helvetica', 'normal');
    const actLines = wrap(safe(report.actionsTaken), pageW - margin * 2);
    doc.text(actLines, leftX, y + 6);
    y += actLines.length * 5 + 10;

    // footer
    doc.setFontSize(9);
    doc.setTextColor(110);
    doc.text(`Generated: ${formatDate(todayISO())}`, margin, pageH - 10);
    doc.setTextColor(0);

    doc.save(`incident-report-${reportNo(report)}.pdf`);
  };

  const handleSave = async () => {
    const required =
      participantId &&
      incidentDate &&
      reportData.incidentType &&
      reportData.description &&
      reportData.location &&
      reportData.severity &&
      reportData.actionsTaken;

    if (!required) {
      showToast('warning', 'Please fill in all required fields.');
      return;
    }

    const participant = participants.find((p) => p.id === participantId);
    const existing = editingId ? reports.find((r) => r.id === editingId) : null;
    const reportNumber = existing?.reportNumber || nextReportId;

    const participantSnapshot = {
      name: participant?.name || 'Unknown',
      ndisNumber: participant?.ndisNumber || 'Unknown',
    };

    const providerSnapshot = {
      name: userInfo?.name || '',
      abn: userInfo?.abn || '',
      ndisProviderNumber: userInfo?.ndisProviderNumber || '',
      address: userInfo?.address || '',
      bankDetails: userInfo?.bankDetails || '',
    };

    const reportPayload = {
      participantId,
      incidentDate,
      ...reportData,
      reportNumber,
      participantSnapshot,
      providerSnapshot,
      updatedAt: serverTimestamp(),
      ...(existing ? {} : { createdAt: serverTimestamp() }),
    };

    try {
      let savedReport;

      if (editingId) {
        await updateItem(currentUser.uid, 'incidentReports', editingId, reportPayload);
        savedReport = { id: editingId, ...reportPayload };
        setReports((prev) => (prev || []).map((r) => (r.id === editingId ? savedReport : r)));
        setEditingId(null);
        showToast('success', 'Report updated. PDF downloaded.');
      } else {
        savedReport = await addItem(currentUser.uid, 'incidentReports', reportPayload);
        setReports((prev) => [...(prev || []), savedReport]);
        await saveNextReportId(nextReportId + 1);
        showToast('success', 'Report created. PDF downloaded.');
      }

      resetForm();
      generatePDF(savedReport);
    } catch (error) {
      console.error('Save failed:', error);
      showToast('error', `Failed to save report: ${error.message}`);
    }
  };

  const handleEdit = (report) => {
    setParticipantId(report.participantId);
    setIncidentDate(report.incidentDate);
    setReportData({
      incidentType: report.incidentType || '',
      description: report.description || '',
      location: report.location || '',
      severity: report.severity || '',
      actionsTaken: report.actionsTaken || '',
    });
    setEditingId(report.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Permanently delete this incident report?')) return;
    try {
      await deleteItem(currentUser.uid, 'incidentReports', id);
      setReports((prev) => (prev || []).filter((r) => r.id !== id));
      showToast('success', 'Report deleted.');
    } catch (error) {
      showToast('error', `Delete failed: ${error.message}`);
    }
  };

  const canSave =
    participantId &&
    incidentDate &&
    reportData.incidentType &&
    reportData.description &&
    reportData.location &&
    reportData.severity &&
    reportData.actionsTaken;

  // ---------- UI ----------
  if (loading) {
    return focusSafeLoading();
  }

  function focusSafeLoading() {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>Loading incident reports...</Typography>
      </Box>
    );
  }

  const selectedParticipant = participants.find((p) => p.id === participantId);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '1200px', mx: 'auto' }}>
      {/* Header */}
      <Box
        sx={{
          mb: 2.5,
          p: 2.5,
          borderRadius: 4,
          border: '1px solid rgba(0,0,0,0.08)',
          background:
            'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(245,247,255,1) 45%, rgba(245,255,248,1) 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <SubtleButton onClick={() => navigate('/')} startIcon={<ArrowBackIcon />}>
            Back
          </SubtleButton>

          <Box sx={{ flex: 1, minWidth: 260 }}>
            <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
              Reports
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)', mt: 0.5 }}>
              Create incident reports, store them, and export clean PDFs.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              <StatPill label="Total" value={stats.total} />
              <StatPill label="This month" value={stats.thisMonth} />
              <StatPill label="High" value={stats.high} />
              <StatPill label="Critical" value={stats.critical} />
            </Box>
          </Box>

          <Chip
            icon={<ReportIcon />}
            label={`Next: ${String(nextReportId).padStart(4, '0')}`}
            sx={{ borderRadius: 999, fontWeight: 900, bgcolor: 'rgba(0,0,0,0.06)' }}
          />
        </Box>
      </Box>

      {/* Create / Edit */}
      <SoftCard
        title={editingId ? 'Edit incident report' : 'Create incident report'}
        icon={<ReportIcon fontSize="small" />}
        right={
          editingId ? (
            <SubtleButton onClick={resetForm} startIcon={<AddIcon />}>
              New report
            </SubtleButton>
          ) : (
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  checked={showArchivedParticipants}
                  onChange={(e) => setShowArchivedParticipants(e.target.checked)}
                />
              }
              label="Show archived participants"
            />
          )
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Top row */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <StyledSelect
              value={participantId}
              onChange={(e) => setParticipantId(e.target.value)}
              displayEmpty
              sx={{ width: { xs: '100%', sm: 380 } }}
              MenuProps={menuProps}
            >
              <MenuItem value="">
                <em>Select participant</em>
              </MenuItem>
              {visibleParticipants.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                  {p.archived ? ' (archived)' : ''}
                </MenuItem>
              ))}
            </StyledSelect>

            <TextField
              label="Incident date"
              type="date"
              value={incidentDate}
              onChange={(e) => setIncidentDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!participantId}
              sx={{ width: { xs: '100%', sm: 220 } }}
            />

            {participantId && (
              <Chip
                label={
                  selectedParticipant
                    ? `For: ${selectedParticipant.name}${selectedParticipant.ndisNumber ? ` (${selectedParticipant.ndisNumber})` : ''}`
                    : 'Participant selected'
                }
                sx={{ borderRadius: 999, fontWeight: 900, bgcolor: 'rgba(0,0,0,0.06)' }}
              />
            )}
          </Box>

          {/* Form */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
            <StyledSelect
              value={reportData.incidentType}
              onChange={handleFormChange('incidentType')}
              displayEmpty
              fullWidth
              disabled={!incidentDate}
              MenuProps={menuProps}
            >
              <MenuItem value="">
                <em>Incident type</em>
              </MenuItem>
              <MenuItem value="Accident">Accident</MenuItem>
              <MenuItem value="Medical Emergency">Medical Emergency</MenuItem>
              <MenuItem value="Behavioral Issue">Behavioral Issue</MenuItem>
              <MenuItem value="Safeguarding Concern">Safeguarding Concern</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </StyledSelect>

            <StyledSelect
              value={reportData.severity}
              onChange={handleFormChange('severity')}
              displayEmpty
              fullWidth
              disabled={!incidentDate}
              MenuProps={menuProps}
            >
              <MenuItem value="">
                <em>Severity</em>
              </MenuItem>
              <MenuItem value="Low">Low</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Critical">Critical</MenuItem>
            </StyledSelect>

            <TextField
              label="Location"
              value={reportData.location}
              onChange={handleFormChange('location')}
              fullWidth
              disabled={!incidentDate}
              sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}
            />

            <TextField
              label="Description"
              multiline
              rows={4}
              value={reportData.description}
              onChange={handleFormChange('description')}
              fullWidth
              disabled={!incidentDate}
              sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}
            />

            <TextField
              label="Actions taken"
              multiline
              rows={4}
              value={reportData.actionsTaken}
              onChange={handleFormChange('actionsTaken')}
              fullWidth
              disabled={!incidentDate}
              sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <PrimaryButton onClick={handleSave} disabled={!canSave} startIcon={<DownloadIcon />}>
              {editingId ? 'Save & Download PDF' : 'Create & Download PDF'}
            </PrimaryButton>

            {editingId && (
              <SubtleButton onClick={resetForm}>
                Cancel edit
              </SubtleButton>
            )}

            {!editingId && (
              <FormControlLabel
                sx={{ ml: 1 }}
                control={
                  <Checkbox
                    checked={showArchivedParticipants}
                    onChange={(e) => setShowArchivedParticipants(e.target.checked)}
                  />
                }
                label="Show archive participants"
              />
            )}
          </Box>
        </Box>
      </SoftCard>

      {/* List */}
      <Paper
        elevation={0}
        sx={{
          mt: 2.5,
          p: 2,
          borderRadius: 4,
          border: '1px solid rgba(0,0,0,0.08)',
          bgcolor: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            Previous reports
          </Typography>
          <Box sx={{ flex: 1 }} />

          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search report / participant / severity / date…"
            sx={{ minWidth: 320 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {filteredReports.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>No reports found</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)' }}>
                Create your first report above, or change your search.
              </Typography>
            </Box>
          ) : (
            filteredReports.map((report) => {
              const participant = participants.find((p) => p.id === report.participantId);
              const name = report.participantSnapshot?.name || participant?.name || 'Unknown';

              const sev = String(report.severity || '').toLowerCase();
              const sevChip =
                sev === 'critical'
                  ? { label: 'Critical', color: 'error' }
                  : sev === 'high'
                  ? { label: 'High', color: 'warning' }
                  : sev === 'medium'
                  ? { label: 'Medium', color: 'info' }
                  : { label: report.severity || 'Low', color: 'default' };

              return (
                <Paper
                  key={report.id}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: '1px solid rgba(0,0,0,0.08)',
                    bgcolor: 'white', // ✅ prevents any black-on-black theme bleed
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ minWidth: 260 }}>
                    <Typography sx={{ fontWeight: 900 }}>
                      {reportNo(report)} — {name}
                    </Typography>

                    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)' }}>
                      {formatDate(report.incidentDate)} • {report.incidentType || '—'} • Location: {report.location || '—'}
                    </Typography>

                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip size="small" label={sevChip.label} color={sevChip.color} variant="outlined" />
                      {report.participantSnapshot?.ndisNumber && (
                        <Chip
                          size="small"
                          label={`NDIS: ${report.participantSnapshot.ndisNumber}`}
                          sx={{ bgcolor: 'rgba(0,0,0,0.04)' }}
                        />
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handleEdit(report)} size="small">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete">
                      <IconButton onClick={() => handleDelete(report.id)} color="error" size="small">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>

                    <PrimaryButton
                      variant="contained"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => generatePDF(report)}
                    >
                      PDF
                    </PrimaryButton>
                  </Box>
                </Paper>
              );
            })
          )}
        </Box>
      </Paper>

      <Snackbar
        open={toast.open}
        autoHideDuration={2600}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Reports;


